import { useCallback, useEffect, useReducer } from 'react'

import { fetchQuest } from './api'
import { classifyCreateRunError } from './createRunError'
import {
  useChooseRewardMutation,
  useCreateRunMutation,
  useFinishRunMutation,
  useUnlockTotemMutation
} from './mutations'
import { buildCreateRunRecord } from './questFormPayload'
import {
  initialQuestPlayerState,
  questPlayerReducer
} from './questPlayerMachine'
import { resolveRewardFlow } from './rewardFlow'
import { clearTotemToken, readTotemToken } from './totemStorage'
import { isQuizStage } from '../constants/stageKind'

import type { QuestFormValues } from './questFormPayload'


export const useQuestPlayerMachine = (eventSlug: string, questUid: string) => {
  const [state, dispatch] = useReducer(questPlayerReducer, initialQuestPlayerState)
  const unlockTotemMutation = useUnlockTotemMutation(eventSlug, questUid)
  const createRunMutation = useCreateRunMutation(eventSlug, questUid)
  const runId = state.run?.id ?? 0
  const finishRunMutation = useFinishRunMutation(eventSlug, questUid, runId)
  const chooseRewardMutation = useChooseRewardMutation(eventSlug, questUid, runId)

  const load = useCallback(async () => {
    const totemToken = readTotemToken(eventSlug, questUid)

    try {
      const { quest, lockedRecord } = await fetchQuest(eventSlug, questUid, totemToken)

      if (lockedRecord) {
        dispatch({ type: 'QUEST_LOAD_LOCKED' })
        return
      }

      dispatch({ type: 'QUEST_LOAD_SUCCEEDED', quest: quest!, totemToken })
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status
      const blocked = status === 404 || status === 403
      dispatch({
        type: 'QUEST_LOAD_FAILED',
        blocked,
        message: blocked ? 'quests.errors.blocked' : 'quests.errors.generic'
      })
    }
  }, [eventSlug, questUid])

  useEffect(() => {
    load()
  }, [load])

  const unlock = useCallback(
    async (pattern: string) => {
      const data = await unlockTotemMutation.mutateAsync(pattern)
      if (data.ok) await load()
      return data
    },
    [unlockTotemMutation, load]
  )

  const start = useCallback(() => dispatch({ type: 'START_REQUESTED' }), [])

  const submitStartRun = useCallback(
    async (values: QuestFormValues) => {
      if (!state.quest) return

      const record = buildCreateRunRecord(state.quest.uniqueness_policy, values.identifier, values.info_responses)
      const totemToken = readTotemToken(eventSlug, questUid)

      try {
        const run = await createRunMutation.mutateAsync({ ...record, totemToken })
        const hasQuizStage = state.quest.quest_execution_stages.some(isQuizStage)
        dispatch({ type: 'RUN_CREATED', run, hasQuizStage })
      } catch (error) {
        const kind = classifyCreateRunError(error)
        if (kind === 'totem_unlock_required') {
          clearTotemToken(eventSlug, questUid)
          dispatch({ type: 'TOTEM_UNLOCK_REQUIRED' })
        }
        throw error
      }
    },
    [state.quest, eventSlug, questUid, createRunMutation]
  )

  const finish = useCallback(async () => {
    if (!state.quest) return

    const run = await finishRunMutation.mutateAsync()
    const flow = resolveRewardFlow(state.quest)

    if (flow === 'none') {
      dispatch({ type: 'RUN_FINISHED', run, nextPhase: 'done' })
      return
    }

    if (flow === 'roulette') {
      const rewardedRun = await chooseRewardMutation.mutateAsync(null)
      dispatch({ type: 'REWARD_CHOSEN', run: rewardedRun })
      return
    }

    dispatch({ type: 'RUN_FINISHED', run, nextPhase: 'choose_reward' })
  }, [state.quest, finishRunMutation, chooseRewardMutation])

  const chooseReward = useCallback(
    async (rewardId: number) => {
      const run = await chooseRewardMutation.mutateAsync(rewardId)
      dispatch({ type: 'REWARD_CHOSEN', run })
    },
    [chooseRewardMutation]
  )

  const restart = useCallback(() => dispatch({ type: 'RESTART_FOR_NEXT_PARTICIPANT' }), [])

  return {
    state,
    reload: load,
    unlock,
    unlockPending: unlockTotemMutation.isPending,
    unlockError: unlockTotemMutation.error,
    start,
    submitStartRun,
    submitStartRunPending: createRunMutation.isPending,
    finish,
    finishPending: finishRunMutation.isPending,
    chooseReward,
    chooseRewardPending: chooseRewardMutation.isPending,
    restart
  }
}
